import { create } from "zustand";
import { persist } from "zustand/middleware";

enum ENUMPOASteps {
  IDENTIFY = 0,
  EMAILANDPHONE = 1,
  POAINFO = 2,
  POADURATION = 3,
  POACONFIRMATION = 4,
  POASIGN = 5,
  BOOKMEETING = 6,
  POASUMMARY = 7,
  CONFIRMATION = 8,
}

type State = {
  currentStep: ENUMPOASteps;
  renewal: boolean;
  // The app-webview variant of the acquisition flow: same steps, app copy and chrome.
  app: boolean;
  completedSteps: Set<ENUMPOASteps>;
};

type Actions = {
  setStep: (step: ENUMPOASteps) => void;
  nextStep: () => void;
  prevStep: () => void;
  resetToDefault: () => void;
  setRenewal: (renewal: boolean) => void;
  setApp: (app: boolean) => void;
  markStepCompleted: (step: ENUMPOASteps) => void;
  canAccessStep: (step: ENUMPOASteps) => boolean;
  getHighestAccessibleStep: () => ENUMPOASteps;
};

type POAStepsStore = State & Actions;

const usePOAStepsStore = create<POAStepsStore>()(
  persist(
    (set, get) => ({
      currentStep: ENUMPOASteps.IDENTIFY,
      renewal: false,
      app: false,
      completedSteps: new Set<ENUMPOASteps>(),
      setStep: (step) => set({ currentStep: step }),
      setRenewal: (renewal) => set({ renewal }),
      setApp: (app) => set({ app }),
      nextStep: () =>
        set((state) => {
          const newCompleted = new Set(state.completedSteps);
          newCompleted.add(state.currentStep);
          return {
            currentStep: state.currentStep + 1,
            completedSteps: newCompleted,
          };
        }),
      prevStep: () => set((state) => ({ currentStep: state.currentStep - 1 })),
      resetToDefault: () =>
        set({
          currentStep: ENUMPOASteps.IDENTIFY,
          completedSteps: new Set<ENUMPOASteps>(),
          renewal: false,
          app: false,
        }),
      markStepCompleted: (step) =>
        set((state) => {
          const newCompleted = new Set(state.completedSteps);
          newCompleted.add(step);
          return { completedSteps: newCompleted };
        }),
      canAccessStep: (step) => {
        const state = get();
        // First step is always accessible
        if (step === ENUMPOASteps.IDENTIFY) return true;
        // For renewal flow, POAINFO is the first accessible step
        if (state.renewal && step === ENUMPOASteps.POAINFO) return true;
        // Can always go back to completed steps
        if (state.completedSteps.has(step)) return true;
        // Can access current step
        if (step === state.currentStep) return true;
        // Can only go to next step if previous step is completed
        const previousStep = step - 1;
        return state.completedSteps.has(previousStep);
      },
      getHighestAccessibleStep: () => {
        const state = get();
        const startStep = state.renewal
          ? ENUMPOASteps.POAINFO
          : ENUMPOASteps.IDENTIFY;
        // Find the highest step that's either completed or is the next accessible one
        let highest = startStep;
        for (let i = startStep; i <= ENUMPOASteps.CONFIRMATION; i++) {
          if (state.completedSteps.has(i)) {
            highest = i + 1;
          } else {
            break;
          }
        }
        return Math.min(highest, ENUMPOASteps.CONFIRMATION) as ENUMPOASteps;
      },
    }),
    {
      name: "poa-steps-storage",
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          return {
            ...parsed,
            state: {
              ...parsed.state,
              completedSteps: new Set(parsed.state.completedSteps),
            },
          };
        },
        setItem: (name, value) => {
          const toStore = {
            ...value,
            state: {
              ...value.state,
              completedSteps: Array.from(value.state.completedSteps),
            },
          };
          sessionStorage.setItem(name, JSON.stringify(toStore));
        },
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    },
  ),
);

export { usePOAStepsStore, ENUMPOASteps };
